/**
 * @module
 * @author vincewu
 * @version 1.0
 * @description 环境变量获取组件
 */
import M_TOOKIT from './../toolkit/index' 

// 默认UA正则
let G_PATT_EXP = {
    app_patt: /fenqile_(ios|android)_(\d{1,}\.\d{1,}\.\d{1,})/i, // app的UA匹配规则
    app_android_patt: /fenqile_android_(\d{1,}\.\d{1,}\.\d{1,})/i, // 安卓app的UA匹配规则
    app_ios_patt: /fenqile_ios_(\d{1,}\.\d{1,}\.\d{1,})/i, // IOS app的UA匹配规则
    sdk_patt: /fenqile_paysdk_(ios|android)/i // SDK的UA匹配规则
} // UA正则规则

// 是否weex环境
const G_IS_WEEX = typeof weex == 'object'
const G_WEEX_ENV = G_IS_WEEX && weex && weex.config && weex.config.env ? weex.config.env : {}

// 是否小程序环境
const G_IS_MP = typeof wx == 'object'
let _mp_env = {}

// 以下参数使用前必须保证在weex环境内，此对象不对外抛出
let _weex_env = {
    /**
     * @member platform
     * @description  [当前运行平台参数]
     * @instance
     * @returns {string}  "Android", "iOS" or "Web"
     * @example
     * M_ENV.platform;
     */
    platform: (G_IS_WEEX && G_WEEX_ENV.platform) || '',

    /**
     * @member osName
     * @description  [操作系统名称]
     * @instance
     * @returns {string}  "Android" or "iOS"
     * @example
     * M_ENV.osName; 
     */
    osName: (G_IS_WEEX && G_WEEX_ENV.osName) || '',

    /**
     * @member deviceWidth
     * @description  [设备宽度]
     * @instance
     * @returns {number}
     * @example
     * M_ENV.deviceWidth; 
     */
    deviceWidth: (G_IS_WEEX && G_WEEX_ENV.deviceWidth) || '',

    /**
     * @member deviceHeight
     * @description  [设备高度]
     * @instance
     * @returns {number}
     * @example
     * M_ENV.deviceHeight; 
     */
    deviceHeight: (G_IS_WEEX && G_WEEX_ENV.deviceHeight) || '',

    /**
     * @member userAgent
     * @description  [用户代理]该值在native端暂未实现
     * @instance
     * @returns {string}
     * @example
     * M_ENV.userAgent; 
     */
    userAgent: (G_IS_WEEX && G_WEEX_ENV.userAgent) || '',

    /**
     * @member weexAppVersion
     * @description  [app版本号]分期乐APP内部会单独封装，不建议业务直接使用该属性
     * @instance
     * @returns {string}
     * @example
     * M_ENV.weexAppVersion; 
     */
    appVersion: (G_IS_WEEX && G_WEEX_ENV.appVersion) || ''
}

if (G_IS_MP) {
    let mp_system_info = wx.getSystemInfoSync() || {}
    let system_arr = mp_system_info.system.split(' ')

    let os_name = system_arr[0]
    let os_version = system_arr[1]

    // 小程序端模拟 userAgent
    // UA 通常格式：Mozilla/5.0 (平台) 引擎版本 浏览器版本号
    let ua_platform = ''
    let ua_engine = 'AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0'
    let ua_browser = `Chrome/ MQQBrowser/ TBS/ Mobile Safari/537.36 NetType/ Language/zh_CN miniProgram`
    // 区分 IOS 和 安卓 和 其他平台
    if (os_name === 'iOS') {
        ua_platform = `iPhone; CPU ${mp_system_info.model} OS ${os_version.replace(/\./g, '_')} like Mac OS X`
    } else if (os_name === 'Android') {
        ua_platform = `Linux; Android ${os_version}; ${mp_system_info.model}; wv`
    } else {
        ua_platform = `${mp_system_info.platform}; ${mp_system_info.system}; x64`
    }
    let user_agent = `Mozilla/5.0 (${ua_platform}) ${ua_engine} ${ua_browser}`

    _mp_env = {
        // platform固定设置为小程序
        platform: 'mp',
        // model 设备值与weex有差异，后续优化
        model: mp_system_info.model,
        osVersion: os_version,
        osName: os_name,
        dpr: mp_system_info.pixelRatio,
        scale: mp_system_info.pixelRatio,
        brand: mp_system_info.brand,
        appName: 'mp_FQL',
        // weex上的设备宽度和高度需要 * 像素比
        deviceWidth: mp_system_info.screenWidth * mp_system_info.pixelRatio,
        deviceHeight: mp_system_info.screenHeight * mp_system_info.pixelRatio,
        // 预留空字段
        userAgent: user_agent
    }
}

// 环境属性
let G_ENV_VAR = {

    // 判断是否weex环境
    get isWeex () {
        return G_IS_WEEX
    },
    
    // 判断是否小程序环境
    get isMp () {
        return G_IS_MP
    },

    // 获取ua native中ua为空
    get userAgent () {
        if (G_IS_WEEX) {
            return _weex_env.userAgent
        } else if (G_IS_MP) {
            return _mp_env.userAgent
        } else {
            return navigator.userAgent
        }
    },

    // 获取设备宽 非app环境才需要获取，app内由app补充
    get deviceWidth () {
        if (G_IS_WEEX) {
            return _weex_env.deviceWidth
        } else if (G_IS_MP) {
            return _mp_env.deviceWidth
        } else {
            return window.screen.width
        }
    },

    // 获取设备高 非app环境才需要获取，app内由app补充
    get deviceHeight () {
        if (G_IS_WEEX) {
            return _weex_env.deviceHeight
        } else if (G_IS_MP) {
            return _mp_env.deviceHeight
        } else {
            return window.screen.height
        }
    },

    // 获取设备品牌
    get deviceBrand () {
        if (G_IS_MP) {
            return _mp_env.brand
        } else {
            return ''
        }
    },

    // 设备型号 String
    get deviceModel () {
        if (G_IS_MP) {
            return _mp_env.model
        } else {
            return ''
        }
    },

    // 获取设备系统
    get deviceSystem () {
        if (G_IS_WEEX) {
            return _weex_env.osName
        } else if (G_IS_MP) {
            return _mp_env.osName
        } else {
            return ''
        }
    },

    // 获取设备系统版本号
    get deviceSystemVersion () {
        if (G_IS_MP) {
            return _mp_env.osVersion
        } else {
            return ''
        }
    },

    // 判断是weex原生在app中
    get isWeexNativeInApp () {
        if (G_IS_WEEX){
            return _weex_env.platform == 'iOS' || _weex_env.platform == 'android'
        }

        return false
    },

    // 判断是weex H5
    get isWeexWeb () {
        if (this.isWeex){
            return _weex_env.platform == 'Web'
        }

        return false
    },

    /**
     * @member isWebInAndroid
     * @description  [当前平台是否为Android平台打开的web页面 包含APP及浏览器]
     * @instance
     * @returns {bool}
     * @example
     * M_ENV.isWebInAndroid; 
     */
    get isWebInAndroid () {
        let res = ''

        if (!this.isWeexNativeInApp) {
            let ua = this.userAgent,
                app_patt = G_PATT_EXP.app_android_patt, // 分期乐安卓APP//支持实例过程传递正则匹配
                navigator_patt = /Android|Adr|Linux/i // 安卓其他浏览器

            res = app_patt.exec(ua) || navigator_patt.exec(ua)
        }

        return !!res
    },

    /**
     * @member isWebInIOS
     * @description  [当前平台是否为IOS平台打开的web页面 包含APP及浏览器]
     * @instance
     * @returns {bool}
     * @example
     * M_ENV.isWebInIOS; 
     */
    get isWebInIOS () {

        let res = ''

        if (!this.isWeexNativeInApp) {
            let ua = this.userAgent,
                app_patt = G_PATT_EXP.app_ios_patt, // 分期乐IOS APP//支持实例过程传递正则匹配
                navigator_patt = /\(i[^;]+;( U;)? CPU.+Mac OS X/ // IOS其他浏览器

            res = app_patt.exec(ua) || navigator_patt.exec(ua)
        }

        return !!res
    },

    // 判断是weex H5在app中
    get isWeexWebInApp () {
        return this.isWebInApp && this.isWeex
    },

    // 判断是H5在app中
    get isWebInApp () {
        let res = ''

        if (!this.isWeexNativeInApp) {
            res = G_PATT_EXP.app_patt.exec(this.userAgent)
        }

        return !!res
    },

    /**
     * @member isSdk
     * @description  [当前是否是SDK环境]
     * @instance
     * @returns {bool}
     * @example
     * M_ENV.isSdk; 
     */
    get isSdk () {
        let res = '',
            ua = this.userAgent,
            patt = G_PATT_EXP.sdk_patt // 支持实例过程传递正则匹配

        res = patt.exec(ua)

        return !!res
    },

    // 判断是否APP环境
    get isInApp () {
        // weex中取weex中env 的 platform ua参数进行判断
        if (G_IS_WEEX) {
            return this.isWeexNativeInApp || this.isWeexWebInApp
        } else { // 判断H5在app内
            let res = G_PATT_EXP.app_patt.exec(this.userAgent)

            return !!res
        }
    },

    /**
     * @member appVersion
     * @description  [app版本号]在分期乐上打开web端可能会匹配到浏览器版本，这里重新适配分期乐App版本
     * @instance
     * @returns {string}
     * @example
     * M_ENV.appVersion; 
     */
    get appVersion () {
        // 若有userAgent则匹配userAgent，无则使用原有version
        if (this.userAgent) {
            // app版本号重新匹配
            let ua = this.userAgent,
                patt = G_PATT_EXP.app_patt, // 支持实例过程传递正则匹配
                res = patt.exec(ua)

            return res && res[2] ? res[2] : ''
        } else if (G_IS_WEEX) {
            return _weex_env.appVersion
        } else {
            return ''
        }
    },

    /**
     * @function isAppVersionBelow
     * @param {string} version - 版本号[必选]
     * @author Henryzeng <henryzeng@lexin.com>
     * @description  app版本号是否低于指定的版本
     * @instance
     * @returns {bool}
     * @example
     * M_ENV.isAppVersionBelow('3.4.1');
     */
    isAppVersionBelow: function (version) {
        return M_TOOKIT.compareVersion(this.appVersion, version) < 0
    },

    /**
     * @function isAppVersionAbove
     * @param {string} version - 版本号[必选]
     * @author Henryzeng <henryzeng@lexin.com>
     * @description  app版本号是否高于指定的版本
     * @instance
     * @returns {bool}
     * @example
     * M_ENV.isAppVersionAbove('3.4.1');
     */
    isAppVersionAbove: function (version) {
        return M_TOOKIT.compareVersion(this.appVersion, version) > 0 // this变量为统一输出的env变量
    }
}

export default G_ENV_VAR